import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/base/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/ui/base/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/base/alert-dialog";
import { useIsMobile } from "@/nucleo/ganchos/use-mobile";
import { cn } from "@/nucleo/utilitarios";
import { ResponsiveModalFooterContext } from "@/ui/useResponsiveModalFooter";

/**
 * Modal responsivo: Dialog no desktop, Drawer (bottom sheet) no mobile.
 *
 * A11y:
 * - Sempre renderiza um título (DialogTitle/DrawerTitle).
 * - Quando `description` não é passada, renderiza uma descrição sr-only
 *   para satisfazer aria-describedby do Radix.
 * - Foco inicial e trap de teclado vêm prontos do Radix.
 * - Escape fecha por padrão; quando `confirmOnClose` está ativo, abre
 *   um AlertDialog de confirmação antes de descartar.
 */
interface ResponsiveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  desktopMaxWidth?: string;
  /** Quando true, qualquer tentativa de fechar abre confirmação. */
  confirmOnClose?: boolean;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export function ResponsiveModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  desktopMaxWidth = "max-w-3xl",
  confirmOnClose = false,
  confirmTitle = "Descartar alterações?",
  confirmDescription = "As alterações feitas neste formulário serão perdidas.",
  confirmLabel = "Descartar",
  cancelLabel = "Continuar editando",
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [registeredFooter, setRegisteredFooter] = React.useState<React.ReactNode | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) setRegisteredFooter(null);
  }, [open]);

  const footerContext = React.useMemo(
    () => ({ setFooter: setRegisteredFooter }),
    [],
  );

  const resolvedFooter = footer ?? registeredFooter;

  /**
   * Captura quem tinha foco no momento em que o usuário pediu para fechar.
   * Assim, quando ele clicar em "Continuar editando", devolvemos o foco para
   * o mesmo elemento (normalmente o botão "Cancelar" do form ou o X do header).
   */
  const requestClose = React.useCallback(() => {
    if (confirmOnClose) {
      const active = document.activeElement as HTMLElement | null;
      // Só guarda se o foco estiver DENTRO do modal — evita devolver foco para
      // algo fora dele (ex.: o botão que abriu o modal).
      if (active && contentRef.current?.contains(active)) {
        lastFocusedRef.current = active;
      } else {
        lastFocusedRef.current = null;
      }
      setConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  }, [confirmOnClose, onOpenChange]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      requestClose();
      return;
    }
    onOpenChange(true);
  };

  const confirmDiscard = () => {
    setConfirmOpen(false);
    // Form state é preservado: só chamamos onOpenChange(false) aqui.
    onOpenChange(false);
  };

  // Restaura o foco no botão Cancelar (ou 1º campo) ao fechar o AlertDialog.
  const restoreFocusOnContinue = (e: Event) => {
    const previous = lastFocusedRef.current;
    if (previous && document.contains(previous)) {
      e.preventDefault();
      previous.focus();
      return;
    }
    // Fallbacks: botão Cancelar dentro do modal, depois 1º campo.
    const root = contentRef.current;
    if (!root) return;
    const cancelBtn = Array.from(
      root.querySelectorAll<HTMLButtonElement>('button[type="button"]')
    ).find((b) => b.textContent?.trim().toLowerCase() === "cancelar");
    if (cancelBtn) {
      e.preventDefault();
      cancelBtn.focus();
      return;
    }
    const firstField = root.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]'
    );
    if (firstField) {
      e.preventDefault();
      firstField.focus();
    }
  };

  const accessibleDescription =
    description ?? <span className="sr-only">Formulário em modal. Pressione Escape para fechar.</span>;

  const confirmDialog = (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent
        onCloseAutoFocus={(e) => {
          // Só restauramos foco se o usuário CANCELOU o descarte
          // (i.e., o modal pai ainda está aberto).
          if (open) restoreFocusOnContinue(e);
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Foco inicial na acao SEGURA (cancelar) de um dialogo destrutivo:
              evita descarte acidental por Enter. Pratica recomendada de
              acessibilidade, que a regra estatica nao distingue. */}
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <AlertDialogCancel autoFocus>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDiscard}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          open={open}
          onOpenChange={handleOpenChange}
          dismissible={!confirmOnClose}
        >
          <DrawerContent ref={contentRef} className="max-h-[90vh] overflow-hidden rounded-t-2xl">
            <DrawerHeader className="text-left pb-2">
              <DrawerTitle className="flex items-center gap-2 text-base">{title}</DrawerTitle>
              <DrawerDescription className="text-sm">
                {accessibleDescription}
              </DrawerDescription>
            </DrawerHeader>

            <ResponsiveModalFooterContext.Provider value={footerContext}>
              {/* Regiao rolavel recebe foco para o teclado poder rolar
                  (WCAG 2.1.1). O rotulo acessivel vem do titulo do modal. */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- regiao rolavel recebe foco para o teclado poder rolar (WCAG 2.1.1) */}
              <div tabIndex={0} role="group" aria-label={typeof title === "string" ? title : "Conteúdo"} className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                {children}
              </div>

              {resolvedFooter && (
                <DrawerFooter className="flex-row justify-end gap-2 border-t border-border bg-background pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                  {resolvedFooter}
                </DrawerFooter>
              )}
            </ResponsiveModalFooterContext.Provider>
          </DrawerContent>
        </Drawer>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          ref={contentRef}
          className={cn(desktopMaxWidth, "flex flex-col max-h-[90vh] overflow-hidden p-0 gap-0")}
          onEscapeKeyDown={(e) => {
            if (confirmOnClose) {
              e.preventDefault();
              setConfirmOpen(true);
            }
          }}
          onPointerDownOutside={(e) => {
            if (confirmOnClose) {
              e.preventDefault();
              setConfirmOpen(true);
            }
          }}
          onInteractOutside={(e) => {
            if (confirmOnClose) {
              e.preventDefault();
            }
          }}
        >
          <ResponsiveModalFooterContext.Provider value={footerContext}>
            <DialogHeader className="min-w-0 shrink-0 border-b border-border px-6 pt-6 pb-4">
              <DialogTitle className="flex items-center gap-2 min-w-0 wrap-break-word">{title}</DialogTitle>
              <DialogDescription className="min-w-0 wrap-break-word [&_strong]:break-all">
                {accessibleDescription}
              </DialogDescription>
            </DialogHeader>

            <div className="min-w-0 w-full flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">{children}</div>

            {resolvedFooter && (
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 min-w-0 flex-wrap shrink-0 border-t border-border bg-background px-6 py-3">
                {resolvedFooter}
              </div>
            )}
          </ResponsiveModalFooterContext.Provider>

        </DialogContent>
      </Dialog>
      {confirmDialog}
    </>
  );
}
